import ReactGA from 'react-ga4';
import { getEnv } from './runtimeConfig';
import { createLogger } from './logger';

const logger = createLogger('GA');

// Real GA4 measurement IDs look like G-XXXXXXXXXX. The env config ships
// "G-XXXXXXXXXX" as a placeholder, which gtag accepts without complaint — hits
// are sent to a property that does not exist and nothing surfaces the mistake.
// Treat the placeholder and any malformed value as "analytics not configured".
const GA4_ID_PATTERN = /^G-[A-Z0-9]{6,12}$/;
const PLACEHOLDER_MEASUREMENT_ID = 'G-XXXXXXXXXX';

// Environments that deliberately run without analytics set one
// of these markers, so an intentional opt-out stays distinguishable from a genuine
// misconfiguration and can be logged quietly.
const DISABLED_MARKERS = new Set(['disabled', 'none', 'off']);

const isAnalyticsOptedOut = (raw: string): boolean => DISABLED_MARKERS.has(raw.trim().toLowerCase());

/**
 * Resolve the configured GA4 measurement ID, or undefined when it is missing,
 * a placeholder, or malformed. Some environments stored the ID with trailing
 * text, so only the first whitespace-delimited token is used.
 */
const getMeasurementId = (): string | undefined => {
  const raw = getEnv('REACT_APP_GA4_MEASUREMENT_ID');
  if (!raw) {
    return undefined;
  }

  const id = raw.trim().split(' ')[0];
  if (!id || id === PLACEHOLDER_MEASUREMENT_ID || !GA4_ID_PATTERN.test(id)) {
    return undefined;
  }

  return id;
};

// Tracking calls are no-ops until initGA() succeeds, otherwise react-ga4 logs an
// "initialize must be called first" warning on every page view and event.
let initialized = false;

export const initGA = () => {
  const raw = getEnv('REACT_APP_GA4_MEASUREMENT_ID');

  if (raw && isAnalyticsOptedOut(raw)) {
    logger.info('Analytics intentionally disabled for this environment');
    return;
  }

  const measurementId = getMeasurementId();
  if (!measurementId) {
    const remedy =
      'Set REACT_APP_GA4_MEASUREMENT_ID to a real GA4 property ID, or to "disabled" to opt out.';
    logger.error(
      raw
        ? `Measurement ID "${raw}" is a placeholder or malformed — analytics disabled. ${remedy}`
        : `No measurement ID configured — analytics disabled. ${remedy}`,
    );
    return;
  }

  ReactGA.initialize(measurementId);
  initialized = true;
};

export const trackPage = (path: string) => {
  if (!initialized) {
    return;
  }
  ReactGA.send({ hitType: 'pageview', page: path });
};

export const trackEvent = (category: string, action: string, label?: string) => {
  if (!initialized) {
    return;
  }
  ReactGA.event({
    category,
    action,
    label,
  });
};

export const setUserId = (userId: string) => {
  if (!initialized) {
    return;
  }
  ReactGA.set({ user_id: userId });
};

/**
 * Get the current Google Analytics session ID
 * @returns Promise that resolves to GA session ID or null if not available
 */
export const getGASessionId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      const measurementId = getMeasurementId();

      // Method 1: Try to get session ID from gtag
      if (typeof window !== 'undefined' && (window as any).gtag && measurementId) {
        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          resolve(getSessionIdFallback());
        }, 3000);

        try {
          (window as any).gtag('get', measurementId, 'session_id', (id: string) => {
            clearTimeout(timeout);

            if (id) {
              resolve(id);
              return;
            }

            // If gtag doesn't return a session ID, fall back to other methods
            resolve(getSessionIdFallback());
          });
        } catch (gtagError) {
          clearTimeout(timeout);
          resolve(getSessionIdFallback());
        }
        return;
      }

      // If gtag is not available, use fallback methods immediately
      resolve(getSessionIdFallback());
    } catch (error) {
      console.warn('Failed to get GA session ID:', error);
      resolve(null);
    }
  });
};

/**
 * Fallback method to get session ID from cookies or generate one
 */
const getSessionIdFallback = (): string | null => {
  try {
    // Method 2: Try to extract from GA cookies
    if (typeof document !== 'undefined') {
      const gaCookies = document.cookie
        .split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => cookie.startsWith('_ga_') || cookie.startsWith('_ga='));

      for (const cookie of gaCookies) {
        const [, value] = cookie.split('=');
        if (value) {
          // Parse GA cookie value to extract session information
          const parts = value.split('.');
          if (parts.length >= 4) {
            // GA4 format: GA1.1.random.timestamp
            const sessionId = `${parts[2]}.${parts[3]}`;
            return sessionId;
          }
        }
      }
    }

    // Method 3: Generate a fallback session identifier
    const fallbackId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
    return fallbackId;
  } catch (error) {
      console.warn('[GA] Failed to get session ID in fallback method:', error);
    return null;
  }
};

/**
 * Get GA client ID (persistent across sessions)
 * @returns Promise that resolves to GA client ID or null if not available
 */
export const getGAClientId = (): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      const measurementId = getMeasurementId();

      if (typeof window !== 'undefined' && (window as any).gtag && measurementId) {
        // Add timeout to prevent hanging
        const timeout = setTimeout(() => {
          resolve(getClientIdFallback());
        }, 3000);

        try {
          (window as any).gtag('get', measurementId, 'client_id', (id: string) => {
            clearTimeout(timeout);

            if (id) {
              resolve(id);
              return;
            }

            // If gtag doesn't return a client ID, fall back to cookie method
            resolve(getClientIdFallback());
          });
        } catch (gtagError) {
          clearTimeout(timeout);
          resolve(getClientIdFallback());
        }
        return;
      }

      // If gtag is not available, use fallback method immediately
      resolve(getClientIdFallback());
    } catch (error) {
      console.warn('Failed to get GA client ID:', error);
      resolve(null);
    }
  });
};

/**
 * Fallback method to get client ID from cookies
 */
const getClientIdFallback = (): string | null => {
  try {
    // Fallback: try to extract from _ga cookie
    if (typeof document !== 'undefined') {
      const gaCookie = document.cookie
        .split(';')
        .find(cookie => cookie.trim().startsWith('_ga='));

      if (gaCookie) {
        const value = gaCookie.split('=')[1];
        const parts = value.split('.');
        if (parts.length >= 4) {
          const clientId = `${parts[2]}.${parts[3]}`;
          return clientId;
        }
      }
    }

    return null;
  } catch (error) {
    console.warn('Failed to get GA client ID from fallback:', error);
    return null;
  }
};
